import {Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroup, NG_VALIDATORS,
  NG_VALUE_ACCESSOR, ValidationErrors, Validators,
} from '@angular/forms';
import {
  NgbDate,
  NgbDatepicker,
  NgbDateStruct,
  NgbTimeStruct
} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent} from '../abstract.component';
import {generateFormId, required} from '@utility';
import {DropdownComponent} from "../dropdown/dropdown.component";
import {OnScroll, OnScrollService} from '@services';

@Component({
  selector: 'app-date-time-input',
  templateUrl: './date-time-input.component.html',
  styleUrls: ['./date-time-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DateTimeInputComponent)
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => DateTimeInputComponent)
    }
  ],
})
export class DateTimeInputComponent extends AbstractComponent implements OnInit, OnChanges, ControlValueAccessor, Validators, OnScroll {

  public static readonly PAGE_SIZE: number = 15;
  currentDate: Date = new Date();

  @Input() openDate?: Date = new Date();
  @Input() displayMilliseconds: boolean = false;
  @Input() displayTime: boolean = false;
  @Input() displaySeconds: boolean = false;
  @Input() label: string = '';
  @Input() maxDate: Date | undefined;
  @Input() minDate: Date | undefined;
  @Input() hideErrorMessage: boolean = false;
  @Input() getErrorMessages?: (errorType: string, key: string) => string;
  @Input() formControlName?: string;
  @Input() openDropdownUpwards: boolean = false;
  @Input() leftAlign: boolean = false;
  @Input() hideLabel: boolean = false;
  @Input() validationActive: boolean = false;
  @Input() closeOnScroll: boolean = false;
  @Input() allowOnlyYearSelection: boolean = false;

  @ViewChild('calendar') calendarInstance?: NgbDatepicker;
  @ViewChild('dropdown', { static: false }) dropdown?: DropdownComponent;

  CalendarState = CalendarState;
  calendarState: CalendarState = CalendarState.DAY;

  months: NavigationObject[] = [];
  years: NavigationObject [] = [];

  dropdownOpen: boolean = false;
  navigationDate: NgbDate = this.getNgbDate(this.currentDate);

  inputForm: FormGroup = new FormGroup<any>({
    inputDate: new FormControl(),
    date: new FormControl(),
    time: new FormControl(),
    milliseconds: new FormControl(),
    onlyYear: new FormControl(),
  });

  invalid: boolean = false;
  errors: ValidationErrors | null = null;
  formId?: string;

  constructor(private controlContainer: ControlContainer, public translocoService: TranslocoService, public onScrollService:OnScrollService) {
    super();
  }

  ngOnInit() {
    this.formId = generateFormId(this.formControlName);
    this.markDisabledDates();
    this.initMonths();
    this.navigationDate = this.getNgbDate(this.openDate);

    this.subscriptions.push(this.inputForm.controls['inputDate'].valueChanges.subscribe(v => {
      v = v ?? undefined
      if (v !== this.inputForm.controls['date'].value) {
        this.inputForm.controls['date'].setValue(v);
        this.inputForm.controls['date'].markAsTouched();
      }
    }));

    if (this.control.hasValidator(required)) {
      this.inputForm.controls['date'].addValidators([required]);
      this.inputForm.controls['date'].updateValueAndValidity();
    }

    if (this.displayTime) {
      this.inputForm.controls['time'].updateValueAndValidity();
    }
    if (this.displayMilliseconds) {
      this.inputForm.controls['milliseconds'].updateValueAndValidity();
    }
    if (this.allowOnlyYearSelection) {
      this.subscriptions.push(this.inputForm.controls['onlyYear'].valueChanges.subscribe(value => {
        if (value) {
          this.changeCalendarState(CalendarState.YEAR);
          const currentValue = this.inputForm.controls['date'].value;
          if (currentValue) {
            const selectedDate = new Date(currentValue);
            this.yearSelected(undefined, selectedDate.getFullYear());
          }
        }
        else {
          this.changeCalendarState(CalendarState.DAY);
         }
        }
      ));
    }


    this.subscriptions.push(this.inputForm.controls['date'].valueChanges.subscribe(_ => {
      const date = this.inputForm.controls['date'].value;
      const time: NgbTimeStruct | undefined = this.inputForm.controls['time'].value;
      const milliseconds: number | undefined = this.inputForm.controls['milliseconds'].value;
      if (date) {
        this.inputForm.controls['inputDate'].setValue(date, {emitEvent: false, onlySelf: true});
      }
      if (!time && date) {
        this.inputForm.controls['time'].setValue({
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: date.getSeconds(),
        });
        this.inputForm.controls['milliseconds'].setValue(date.getMilliseconds());
      } else {
        this.onChange(this.getTime(date, time, milliseconds));
      }
    }));
    this.subscriptions.push(this.inputForm.controls['time'].valueChanges.subscribe(_ => {
      const date = this.inputForm.controls['date'].value;
      const time: NgbTimeStruct | undefined = this.inputForm.controls['time'].value;
      const milliseconds: number | undefined = this.inputForm.controls['milliseconds'].value;
      if (date && time) {
        this.onChange(this.getTime(date, time, milliseconds));
      }
    }));
    this.subscriptions.push(this.inputForm.controls['milliseconds'].valueChanges.subscribe(_ => {
      const date = this.inputForm.controls['date'].value;
      const time: NgbTimeStruct | undefined = this.inputForm.controls['time'].value;
      const milliseconds: number | undefined = this.inputForm.controls['milliseconds'].value;
      if (date && time) {
        this.onChange(this.getTime(date, time, milliseconds));
      }
    }));
    if (this.closeOnScroll) {
      this.onScrollService.addOnScrollListener(this);
    }
  }


  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['maxDate']?.currentValue !== changes['maxDate']?.previousValue ||
      changes['minDate']?.currentValue !== changes['minDate']?.previousValue ||
      changes['startDateTime']?.currentValue !== changes['startDateTime']?.previousValue
    ) {
      this.markDisabledDates();
      if (this.control) {
        this.control.updateValueAndValidity({ emitEvent: this.validationActive});
      }
    }
  }

  changeCalendarState(calendarState: CalendarState) {
    if (this.inputForm.controls['onlyYear'].value) {
      this.calendarState = CalendarState.YEAR;
      this.initYears();
    }
    else if (this.calendarState === calendarState) {
      this.calendarState = CalendarState.DAY
    } else {
      this.calendarState = calendarState;
      if (calendarState === CalendarState.YEAR) {
        this.initYears();
      }
      if (calendarState === CalendarState.MONTH) {
        this.updateMonths();
      }
    }
  }

  initMonths() {
    this.months = [];
    for (let i = 1; i <= 12; i++) {
      this.months.push(new NavigationObject(i, false))
    }
  }

  updateMonths() {
    const maxDate: NgbDate = this.maxCalendarDate;
    const minDate: NgbDate = this.minCalendarDate;
    for (let month of this.months) {
      month.disabled = false;
      if (this.navigationDate.year === maxDate.year) {
        if (month.value > maxDate.month) {
          month.disabled = true;
        }
      }
      if (this.navigationDate.year === minDate.year) {
        if (month.value < minDate.month) {
          month.disabled = true;
        }
      }
    }
  }

  initYears() {
    let from: number = this.navigationDate.year - 7;
    let to: number = this.navigationDate.year + 7;
    this.years = [];
    for (let i = from; i <= to; i++) {
      this.years.push(new NavigationObject(i, i < this.minCalendarDate.year || i > this.maxCalendarDate.year));
    }
  }

  yearSelected(event: Event | undefined, year: number) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    let selectedDate : Date | undefined;
    this.navigationDate.year = year;
    const currentValue = this.inputForm.controls['date'].value;
    if (this.inputForm.controls['onlyYear'].value) {
      this.calendarState = CalendarState.YEAR;
      selectedDate = new Date(year, 0, 1, 0, 0, 0, 0);
    }
    else if (currentValue) {
      this.calendarState = CalendarState.DAY;
      selectedDate = new Date(currentValue);
      const month = selectedDate.getMonth();

      selectedDate.setFullYear(year);

      // if day overflowed
      if (selectedDate.getMonth() !== month) {
        const lastDay = new Date(year, month + 1, 0).getDate();
        selectedDate = new Date(year, month, lastDay);
      }
    }
    if (selectedDate) {
      this.updateNavigationAndForm(selectedDate);
    }
    this.calendarInstance?.navigateTo(this.navigationDate);
  }

  monthSelected(event: Event, month: number) {
    event.preventDefault();
    event.stopPropagation();

    this.navigationDate.month = month;
    this.calendarState = CalendarState.DAY;

    const currentValue = this.inputForm.controls['date'].value;
    if (currentValue) {
      let selectedDate = new Date(currentValue);
      const originalDay = selectedDate.getDate();
      const year = selectedDate.getFullYear();

      selectedDate.setMonth(month - 1);

      if (selectedDate.getDate() !== originalDay) {
        const lastDay = new Date(year, month, 0).getDate();
        selectedDate = new Date(year, month - 1, lastDay);
      }

      this.updateNavigationAndForm(selectedDate);
    }
    this.calendarInstance?.navigateTo(this.navigationDate);
  }

  private updateNavigationAndForm(date: Date) {
    this.navigationDate = {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    } as NgbDate;
    this.inputForm.controls['date'].setValue(date);
  }

  navigateNext() {
    switch (this.calendarState) {
      case CalendarState.DAY:
        this.navigateToNextMonth();
        break;
      case CalendarState.YEAR:
        this.navigateNextYears();
        break;
    }
  }

  navigateNextYears(): void {
    if (this.years.at(this.years.length - 1)?.value && this.years.at(this.years.length - 1)!.value < this.maxCalendarDate.year) {
      let from = this.years.at(this.years.length - 1)!.value;
      let to = from + DateTimeInputComponent.PAGE_SIZE
      this.years = [];
      for (let i = from + 1; i <= to; i++) {
        this.years.push(new NavigationObject(i, i > this.maxCalendarDate.year));
      }
    }
  }

  navigatePrevious() {
    switch (this.calendarState) {
      case CalendarState.DAY:
        this.navigateToPreviousMonth();
        break;
      case CalendarState.YEAR:
        this.navigatePreviousYears();
        break;
    }
  }

  navigatePreviousYears(): void {
    if (this.years.at(0)?.value && this.years.at(0)!.value > this.minCalendarDate.year) {
      let to = this.years.at(0)!.value;
      let from = to - DateTimeInputComponent.PAGE_SIZE
      this.years = [];
      for (let i = from; i < to; i++) {
        this.years.push(new NavigationObject(i, i < this.minCalendarDate.year));
      }
    }
  }

  navigateToPreviousMonth() {
    if (this.calendarInstance?.calendar) {
      const previous: NgbDate = this.calendarInstance.calendar.getPrev(this.navigationDate, 'm');
      if (previous.year > this.minCalendarDate.year || (previous.year == this.minCalendarDate.year && previous.month >= this.minCalendarDate.month)) {
        this.navigationDate = previous;
        this.calendarInstance?.navigateTo(this.navigationDate);
      }
    }
  }

  navigateToNextMonth() {
    if (this.calendarInstance?.calendar) {
      let next: NgbDate = this.calendarInstance.calendar.getNext(this.navigationDate, 'm');
      const min : NgbDate = this.getNgbDate(this.minDate)
      if (next.before(min)) {
        next = min;
      }
      if (next.year < this.maxCalendarDate.year || (next.year == this.maxCalendarDate.year && next.month <= this.maxCalendarDate.month)) {
        this.navigationDate = next;
        this.calendarInstance?.navigateTo(this.navigationDate);
      }
    }
  }


  onTouched: Function = () => {
  };
  onChange: Function = (value: Date | undefined) => {
  };

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: Function) {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean) {
    if (disabled) {
      this.inputForm.disable();
    } else {
      this.inputForm.enable();
    }
  }

  get control(): FormControl {
    return <FormControl<any>>this.controlContainer!.control!.get(this.formControlName!);
  }

  writeValue(value: any) {
    if (value) {
      let date: Date = new Date(value)
      if (date) {
        let time: NgbTimeStruct = {
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: date.getSeconds(),
        }
        this.inputForm.controls["time"].setValue(time);
        this.inputForm.controls["milliseconds"].setValue(date.getMilliseconds());
        date.setHours(0, 0, 0, 0);
        this.inputForm.controls["date"].setValue(date);
      }
    } else {
      this.inputForm.controls["time"].setValue(undefined);
      this.inputForm.controls["date"].setValue(undefined);
      this.inputForm.controls["milliseconds"].setValue(undefined);
    }
    if (this.control) {
      this.control.updateValueAndValidity({ emitEvent: this.validationActive });
    }
  }

  markDisabled = (date: NgbDateStruct) => false;

  markDisabledDates() {
    this.markDisabled = (date: NgbDateStruct) => {
      if (this.inputForm.controls['date'].value) {
        let selectedDate = new Date(this.inputForm.controls['date'].value);
        let struct = {
          year: selectedDate.getFullYear(),
          month: selectedDate.getMonth() + 1,
          day: selectedDate.getDate()
        };
        if ((date as NgbDate).equals(struct)) {
          return false;
        }
      }
      if ((date as NgbDate).after(this.maxCalendarDate)) return true;
      return (date as NgbDate).before(this.minCalendarDate);
    };
  }

  dropdownClosed(open: boolean) {
    this.inputForm.markAsTouched();
    this.dropdownOpen = open;
  }

  onDateSelected(date: NgbDateStruct): void {
    if (this.dropdown) {
      this.dropdown.close();
      this.dropdownClosed(false); // Close the dropdown
    }
  }

  initCalendar() {
    this.navigationDate = this.getCalendarStartDate();
    this.calendarState = CalendarState.DAY;
  }

  getCalendarStartDate(): NgbDate {
    let navigationDate: Date = this.openDate ?? this.currentDate;
    if (this.minDate && this.minDate.getTime() > navigationDate.getTime()) {
      navigationDate = this.minDate;
    }
    if (this.maxDate && this.maxDate.getTime() < navigationDate.getTime()) {
      navigationDate = this.maxDate;
    }
    return this.getNgbDate(this.inputForm.controls['date']?.value ?? navigationDate);
  }


  hasErrors() {
    return this.inputForm.errors || this.control.errors || this.errors;
  }

  get maxCalendarDate(): NgbDate {
    const date: Date = new Date(this.currentDate);
    return this.getNgbDate(this.maxDate ?? new Date(date.setFullYear(date.getFullYear() + 10)));
  }

  get minCalendarDate(): NgbDate {
    const date: Date = new Date(this.currentDate);
    return this.getNgbDate(this.minDate ?? new Date(date.setFullYear(date.getFullYear() - 100)));
  }

  getNgbDate(date?: Date): NgbDate {
    date = new Date(date ?? this.currentDate);
    return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
  }

  getTime(date: Date | undefined, time: NgbTimeStruct | undefined, milliseconds: number|undefined): Date | undefined {
    if (!date) {
      return undefined;
    }

    const returnDate = new Date(date);
    returnDate.setHours(0,0,0,0)

    if (time) {
      returnDate.setHours(time.hour, time.minute, time.second, milliseconds || 0);
    }

    return returnDate;
  }


  isTouched(): boolean {
    return this.inputForm.touched || this.control.touched || this.inputForm.controls['inputDate'].touched;
  }

  onIncrementMilliseconds(incrementNumber: number) {
    const date = this.inputForm.controls['date'].value;
    const time: NgbTimeStruct | undefined = this.inputForm.controls['time'].value;
    const milliseconds: number | undefined = this.inputForm.controls['milliseconds'].value;
    if (date && time) {
      let returnDate: Date | undefined = this.getTime(date, time, milliseconds);
      returnDate?.setMilliseconds((returnDate?.getMilliseconds() ?? 0) + incrementNumber);
      this.writeValue(returnDate);
      this.onChange(returnDate);
    }
  }

  private onValidatorChange: () => void = () => {};

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    let errors: ValidationErrors = {};
    const parsedDate = control.value ? new Date(control.value) : undefined;
    const selectedDate = parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate : undefined;
    const isRequired = control.hasValidator(required)
      if (!selectedDate) {
        if (isRequired) {
          errors['required'] = true
        }
      } else {
        if (this.minDate) {
          if (selectedDate.getTime() < this.minDate.getTime()) {
            errors['date'] = {min: this.minDate};
          }
        }
        if (this.maxDate) {
          if (selectedDate.getTime() > this.maxDate.getTime()) {
            errors['date'] = {max: this.maxDate};
          }
        }
      }

      if (Object.keys(errors).length > 0) {
        this.errors = errors
        this.invalid = true;
      } else {
        this.errors = null;
        this.invalid = false;
      }
      return this.errors;
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.closeOnScroll) {
      this.onScrollService.removeOnScrollListener(this);
    }
  }

  onWindowScroll = () => {
    if (this.closeOnScroll && this.dropdown) {
      this.dropdown.close();
      this.dropdownClosed(false);
    }
  };
}

enum CalendarState {
  DAY,
  MONTH,
  YEAR
}

class NavigationObject {
  constructor(public value: number, public disabled: boolean) {
  }
}
